import * as React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/auth/AuthContext'
import { AuthShell } from '@/components/AuthShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { authService } from '@/services/AuthService'

/**
 * The page the email confirmation link opens (docs/public-site.spec.md,
 * "Vendor signup"). Like the sign-in link's page (`MagicLinkPage`), it confirms
 * on a tap rather than on arrival: mail security that opens every link to scan
 * it would otherwise use the link up first.
 */
export function ConfirmEmailPage() {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const [token] = React.useState<string>(() => params.get('token') ?? '')
  const [state, setState] = React.useState<'waiting' | 'confirming' | 'done'>('waiting')
  const [error, setError] = React.useState<string | null>(null)

  // The token is the credential: out of the address bar once read, so it isn't
  // left in history or shared by copying the address.
  React.useEffect(() => {
    if (params.has('token')) window.history.replaceState(null, '', '/confirm-email')
  }, [params])

  async function confirm(): Promise<void> {
    setState('confirming')
    setError(null)
    try {
      await authService.confirmEmail({ token })
      // The banner asking for it goes, and asking venues opens up.
      await queryClient.invalidateQueries()
      setState('done')
    } catch (cause) {
      setError(requestErrorMessage(cause))
      setState('waiting')
    }
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-display">{state === 'done' ? 'Email confirmed' : 'Confirm your email'}</CardTitle>
          <CardDescription>
            {state === 'done'
              ? 'Venues can now find your company in their search, and you can ask them to work with you.'
              : 'Confirm it to let venues find your company and to ask them to work with you.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'done' ? (
            <Button asChild className="w-full">
              <Link to={isAuthenticated ? '/vendor' : '/login'}>{isAuthenticated ? 'Go to your company' : 'Sign in'}</Link>
            </Button>
          ) : (
            <Button pending={state === 'confirming'} className="w-full" disabled={token === ''} onClick={() => void confirm()}>
              Confirm my email
            </Button>
          )}
          {token === '' ? <FormError>This link is missing its code. Open it from the email again.</FormError> : null}
          {error === null ? null : (
            <FormError>
              {error} {isAuthenticated ? 'You can send a new link from the top of any page.' : 'Sign in to send a new one.'}
            </FormError>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  )
}
